import { HOST_URL } from '../config'
import { HttpStatus } from '../constants/http-status.enum'
import type { ApiResponse } from './index'

export async function restartServer(): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}admin/restart`, {
        method: 'POST',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<null>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export interface UsbFlasherAccessInfo {
    code: string
    remainingSeconds: number
    periodSeconds: number
}

export async function getUsbFlasherAccess(): Promise<ApiResponse<UsbFlasherAccessInfo>> {
    const response = await fetch(`${HOST_URL}admin/usb-flasher-access`, {
        method: 'GET',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<UsbFlasherAccessInfo>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function clearSecurityCounters(): Promise<ApiResponse<{ authCountersCleared: number; rateLimitCountersCleared: number }>> {
    const response = await fetch(`${HOST_URL}admin/security-counters/clear`, {
        method: 'POST',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<{ authCountersCleared: number; rateLimitCountersCleared: number }>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export interface AdminLicense {
    id: number
    licenseKey: string
    plan: string
    durationDays: number
    required: number
    revoked: number
    assignedDeviceId?: string | null
    activatedAt?: string | null
    expiresAt?: string | null
    tempUnlockUntil?: string | null
    tempUnlockUsedForCycle: number
    cycleId: number
    notes?: string | null
    createdAt: string
    updatedAt: string
}

export async function listLicenses(): Promise<ApiResponse<AdminLicense[]>> {
    const response = await fetch(`${HOST_URL}admin/licenses`, {
        method: 'GET',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<AdminLicense[]>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function createLicense(payload: {
    licenseKey?: string
    plan?: string
    durationDays?: number
    required?: number
    notes?: string
}): Promise<ApiResponse<AdminLicense>> {
    const response = await fetch(`${HOST_URL}admin/licenses/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
    })
    const result = (await response.json()) as ApiResponse<AdminLicense>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateLicense(payload: {
    id: number
    licenseKey?: string
    plan?: string
    durationDays?: number
    required?: number
    revoked?: number
    assignedDeviceId?: string
    notes?: string
    expiresAt?: number | null
}): Promise<ApiResponse<AdminLicense>> {
    const response = await fetch(`${HOST_URL}admin/licenses/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
    })
    const result = (await response.json()) as ApiResponse<AdminLicense>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function revokeLicense(id: number, revoked: number): Promise<ApiResponse<AdminLicense>> {
    const response = await fetch(`${HOST_URL}admin/licenses/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, revoked }),
    })
    const result = (await response.json()) as ApiResponse<AdminLicense>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function deleteLicense(id: number): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}admin/licenses/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
    })
    const result = (await response.json()) as ApiResponse<null>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
